import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Star, 
  Loader2, 
  CheckCircle, 
  AlertTriangle,
  ArrowLeft
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const ReviewPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [bookingInfo, setBookingInfo] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");

  useEffect(() => {
    fetchReviewData();
  }, [token]);

  const fetchReviewData = async () => {
    try {
      const response = await axios.get(`${API}/public/review/${token}`);
      setBookingInfo(response.data);
      setExistingReview(response.data.existing_review);
      if (response.data.existing_review) {
        setRating(response.data.existing_review.rating);
        setComment(response.data.existing_review.comment || "");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Prenotazione non trovata");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Seleziona una valutazione");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/review/${token}`, { rating, comment });
      setSubmitted(true);
      toast.success("Grazie per la tua recensione!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio della recensione");
    } finally {
      setSubmitting(false);
    }
  };

  const parseDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr.replace("Z", ""));
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Impossibile lasciare recensione</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Link to="/">
              <Button>Torna alla Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already submitted or existing review
  if (submitted || existingReview) {
    const reviewToShow = existingReview || { rating, comment };
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Grazie per la tua recensione!</h2>
            <p className="text-slate-500 mb-4">
              La tua opinione ci aiuta a migliorare il servizio.
            </p>
            
            {/* Show the review */}
            <div className="bg-slate-50 rounded-lg p-4 text-left mt-4">
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => (
                  <Star 
                    key={i} 
                    className={`h-5 w-5 ${i <= reviewToShow.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} 
                  />
                ))}
              </div>
              {reviewToShow.comment && (
                <p className="text-sm text-slate-600 italic">"{reviewToShow.comment}"</p>
              )}
            </div>
            
            <Link to="/" className="mt-6 inline-block">
              <Button variant="outline">Torna alla Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const appointmentDate = parseDateTime(bookingInfo?.date);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          {bookingInfo?.business?.logo_url && (
            <img 
              src={bookingInfo.business.logo_url} 
              alt={bookingInfo.business.name}
              className="h-16 w-16 rounded-full mx-auto mb-3 object-cover"
            />
          )}
          <h1 className="text-2xl font-bold text-slate-900">Lascia una Recensione</h1>
          <p className="text-slate-500 text-sm mt-1">{bookingInfo?.business?.name}</p>
        </div>

        {/* Booking Summary */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <p className="text-sm text-slate-600">
              <strong>Appuntamento del:</strong>{" "}
              {appointmentDate && format(appointmentDate, "d MMMM yyyy 'alle' HH:mm", { locale: it })}
            </p>
            <p className="text-sm text-slate-600 mt-1">
              <strong>Servizi:</strong> {bookingInfo?.services?.join(", ")}
            </p>
          </CardContent>
        </Card>

        {/* Review Form */}
        <Card>
          <CardHeader>
            <CardTitle>Come valuti la tua esperienza?</CardTitle>
            <CardDescription>
              Seleziona da 1 a 5 stelle e lascia un commento opzionale
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Star Rating */}
            <div className="flex justify-center gap-2">
              {[1,2,3,4,5].map(i => (
                <button
                  key={i}
                  type="button"
                  className="p-1 transition-transform hover:scale-110"
                  onMouseEnter={() => setHoverRating(i)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(i)}
                >
                  <Star 
                    className={`h-10 w-10 transition-colors ${
                      i <= (hoverRating || rating) 
                        ? "fill-amber-400 text-amber-400" 
                        : "text-slate-300"
                    }`} 
                  />
                </button>
              ))}
            </div>
            
            {/* Rating Label */}
            <p className="text-center text-sm text-slate-500">
              {rating === 1 && "Pessimo"}
              {rating === 2 && "Scarso"}
              {rating === 3 && "Nella media"}
              {rating === 4 && "Buono"}
              {rating === 5 && "Eccellente!"}
              {rating === 0 && "Seleziona una valutazione"}
            </p>

            {/* Comment */}
            <div>
              <label className="text-sm font-medium">Il tuo commento (opzionale)</label>
              <Textarea
                placeholder="Racconta la tua esperienza..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="mt-2"
                rows={4}
              />
            </div>

            {/* Submit */}
            <Button 
              className="w-full" 
              size="lg"
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Star className="h-4 w-4 mr-2" />
              )}
              Invia Recensione
            </Button>
          </CardContent>
        </Card>

        {/* Back link */}
        <div className="text-center mt-6">
          <Link to={`/manage-booking/${token}`} className="text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-4 w-4 inline mr-1" />
            Torna ai dettagli prenotazione
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
